import * as React from "react";
import { TableCell } from "@mui/material";
import { DataTable, type DataTableColumn } from "@/components/dashboard/DataTable";
import { PriceCell } from "@/components/dashboard/PriceCell";
import { HealthBadge } from "@/components/dashboard/HealthBadge";

export interface WatchlistRow {
  instrument_token: string;
  trading_symbol: string;
  exchange_code: string;
  last_price?: number | null;
  provider?: string | null;
  updateStatus: "success" | "loading" | "error";
}

interface WatchlistTableProps {
  rows: WatchlistRow[];
}

const notReported = <span style={{ fontStyle: "italic", opacity: 0.6 }}>Not reported by backend</span>;

/**
 * `QuoteDto` carries no previous-close/change field, so Change and
 * Change % are never computed here (that would be a forbidden
 * client-side calculation) - both render "Not reported by backend."
 */
export function WatchlistTable({ rows }: WatchlistTableProps): React.JSX.Element {
  const columns: Array<DataTableColumn<WatchlistRow>> = [
    {
      key: "symbol",
      label: "Symbol",
      sortable: true,
      sortValue: (row) => row.trading_symbol,
      render: (row) => <TableCell key="symbol">{row.trading_symbol}</TableCell>,
    },
    {
      key: "exchange",
      label: "Exchange",
      sortable: true,
      sortValue: (row) => row.exchange_code,
      render: (row) => <TableCell key="exchange">{row.exchange_code}</TableCell>,
    },
    {
      key: "last_price",
      label: "Last Price",
      align: "right",
      render: (row) => <PriceCell key="last_price" value={row.last_price} />,
    },
    {
      key: "change",
      label: "Change",
      render: () => <TableCell key="change">{notReported}</TableCell>,
    },
    {
      key: "change_pct",
      label: "Change %",
      render: () => <TableCell key="change_pct">{notReported}</TableCell>,
    },
    {
      key: "provider",
      label: "Provider",
      render: (row) => <TableCell key="provider">{row.provider ?? notReported}</TableCell>,
    },
    {
      key: "update_status",
      label: "Update Status",
      render: (row) => (
        <TableCell key="update_status">
          <HealthBadge
            status={row.updateStatus === "success" ? "ok" : row.updateStatus === "error" ? "failed" : "pending"}
          />
        </TableCell>
      ),
    },
  ];

  return (
    <DataTable
      ariaLabel="Watchlist"
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.instrument_token}
    />
  );
}
